import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/Base/Dialog";
import { Button } from "@/components/Base/Button";

export interface LabelChoiceDialogSubtitle {
  roNumber?: string;
  vehicleDescription?: string;
}

/**
 * A hook providing an imperative label-choice picker for the move-time
 * bidirectional-label popup: `chooseLabel(labels, subtitle)` opens a dialog
 * with a radio option per label and resolves with whichever the user selects
 * and confirms, or rejects if they cancel. Modeled on `useConfirmDialog`
 * (`src/components/Common/ConfirmDialog.tsx`), but the returned `Dialog` is
 * memoized on the picker's own state rather than recreated every render of
 * the caller — see the design spec's risk #3 (docs/superpowers/specs/
 * 2026-08-29-webv3-237-move-time-label-choice-popup-design.md) for why:
 * unlike `useConfirmDialog`'s existing callers, this hook is used from
 * `Dashboard.tsx`, which re-renders on every live board update, and an
 * unstable component reference there would remount the picker mid-choice.
 * `subtitle` is kept as primitive strings (not the caller's `card` object)
 * so that risk stays avoided even as this hook threads RO/vehicle context
 * through.
 * @returns {object} `chooseLabel` and the `Dialog` component to render.
 */
export function useLabelChoiceDialog() {
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [labels, setLabels] = useState<string[]>([]);
  const [subtitle, setSubtitle] = useState<LabelChoiceDialogSubtitle>({});
  const [handlers, setHandlers] = useState<{
    onChoose: (label: string) => void;
    onCancel: () => void;
  }>({
    onChoose: () => {},
    onCancel: () => {},
  });

  const chooseLabel = useCallback(
    (
      candidateLabels: string[],
      nextSubtitle: LabelChoiceDialogSubtitle = {}
    ) => {
      return new Promise<string>((resolve, reject) => {
        setLabels(candidateLabels);
        setSubtitle(nextSubtitle);
        setHandlers({
          onChoose: (label) => {
            resolve(label);
            setDialogOpen(false);
          },
          onCancel: () => {
            reject();
            setDialogOpen(false);
          },
        });
        setDialogOpen(true);
      });
    },
    []
  );

  const PickerDialog = useMemo(
    () => () => (
      <LabelChoiceDialog
        open={isDialogOpen}
        setOpen={setDialogOpen}
        labels={labels}
        subtitle={subtitle}
        onChoose={handlers.onChoose}
        onCancel={handlers.onCancel}
      />
    ),
    [isDialogOpen, labels, subtitle, handlers]
  );

  return { chooseLabel, Dialog: PickerDialog };
}

export interface LabelChoiceDialogProps {
  labels: string[];
  subtitle?: LabelChoiceDialogSubtitle;
  onChoose: (label: string) => void;
  onCancel: () => void;
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const LabelChoiceDialog = ({
  labels,
  subtitle = {},
  onChoose,
  onCancel,
  open = false,
  setOpen,
}: LabelChoiceDialogProps) => {
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);

  useEffect(() => {
    if (open) setSelectedLabel(null);
  }, [open]);

  const subtitleText = [
    subtitle.roNumber ? `RO ${subtitle.roNumber}` : null,
    subtitle.vehicleDescription || null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
        setOpen(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Which of these labels should we sync back to Tekmetric?
          </DialogTitle>
          {subtitleText && (
            <DialogDescription>{subtitleText}</DialogDescription>
          )}
        </DialogHeader>
        <div className="flex flex-col gap-2">
          {labels.map((label) => (
            <label key={label} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="label-choice"
                checked={selectedLabel === label}
                onChange={() => setSelectedLabel(label)}
              />
              {label}
            </label>
          ))}
        </div>
        <DialogFooter className="gap-2 sm:space-x-0">
          <Button
            type={"button"}
            variant={"secondary"}
            className="w-full"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type={"button"}
            className="w-full"
            disabled={!selectedLabel}
            onClick={() => selectedLabel && onChoose(selectedLabel)}
          >
            Sync and move
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
